import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as schoolMemberSelector from '../../../../state/selectors/member.school.selector';
import * as paginationSelector from '../../../../state/selectors/pagination.selector';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { LoadingMemberComponent } from '../../../components/loaders/loading-member/loading-member.component';
import { DlgViewProfileComponent } from '../../../profile/dlg-view-profile/dlg-view-profile.component';
import { DlgAssignRolesComponent } from '../../dlg-assign-roles/dlg-assign-roles.component';
import { ChangeMemberStatusComponent } from '../change-member-status/change-member-status.component';

@Component({
  selector: 'app-list-faculty',
  standalone: true,
  imports: [
    AsyncPipe,
    InitialsPipe,
    RouterLink,
    TitleCasePipe,
    MatDialogModule,
    BtnMoreComponent,
    BtnFabComponent,
    BtnChipComponent,
    ListUtilsBarComponent,
    ChangeMemberStatusComponent,
    NgxSkeletonLoaderModule,
    MomentPipe,
    LoadingMemberComponent,
  ],
  templateUrl: './list-faculty.component.html',
  styleUrl: './list-faculty.component.css',
})
export class ListFacultyComponent implements OnInit, OnDestroy {
  firstNewItemId: string | null = null;
  private cdr = inject(ChangeDetectorRef);
  @ViewChildren('itemRef') itemRefs!: QueryList<ElementRef>;

  route: ActivatedRoute = inject(ActivatedRoute);
  toastr: ToastrService = inject(ToastrService);
  roles: SchoolRolesService = inject(SchoolRolesService);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  private dialog: MatDialog = inject(MatDialog);

  members$: Observable<SchoolMember[] | null | undefined> = new Observable();

  loading$: Observable<boolean> = new Observable();
  isSearching$: Observable<boolean> = new Observable();
  showingSearchResults$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  group: Group | null | undefined;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;
  utils = UtilsService;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject<void>();

  inviteText: string =
    'Hi, I would like to invite you to join our school group on AtChurch. Please click on the link below to join. https://web.atchurch.app/links/groupinvite?groupId=';

  constructor(private store: Store<AppState>) {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');

      this.store
        .select(groupSelector.getGroupById(this.groupId!))
        .pipe(takeUntil(this.exit$))
        .subscribe((group) => {
          this.group = group;
          this.inviteText = `Hi, I would like to invite you to join our school group '${this.group?.name}' on AtChurch. Please click on the link below to join. https://${environment.domain}/links/groupinvite?groupId=`;
        });
    });

    this.loading$ = this.store.select(schoolMemberSelector.loading);

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.loadMembers();
  }

  ngOnInit() {
    this.members$.pipe(take(1)).subscribe((members) => {
      if (members?.length === 0) {
        this.store.dispatch(
          memberActions.getMembers({
            source: 'ListFacultyComponent :: ngOnInit',
            groupId: this.groupId!,
            status: [MembershipStatus.active],
            getMemberType: [
              SchoolMemberType.president,
              SchoolMemberType.principal,
              SchoolMemberType.teacher,
            ],
          }),
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  loadMembers() {
    this.members$ = this.store.select(
      schoolMemberSelector.getGroupMembersByType({
        memberType: [
          SchoolMemberType.president,
          SchoolMemberType.principal,
          SchoolMemberType.teacher,
        ],
        groupId: this.groupId,
        status: [MembershipStatus.active],
      }),
    );
  }

  addMore() {
    this.store
      .select(paginationSelector.getPaginationById(this.groupId!))
      .pipe(take(1))
      .subscribe((paginationRecord) => {
        if (paginationRecord?.lastFacultyRecord?.id) {
          this.firstNewItemId = paginationRecord.lastFacultyRecord.id;
        }

        this.store.dispatch(
          memberActions.getMembers({
            source: 'ListFacultyComponent :: addMore',
            groupId: this.groupId!,
            getMemberType: [
              SchoolMemberType.president,
              SchoolMemberType.principal,
              SchoolMemberType.teacher,
            ],
            lastRecord: paginationRecord,
          }),
        );
      });
  }

  onAssignRole(item: SchoolMember) {
    let data: any = Object.values(SchoolMemberType);
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Member Role(s)',
      description: `Select one or more roles to assign to '${item.name}'`,
      action: 'Update Roles',
      member: item,
    };
    const dialogRef = this.dialog.open(DlgAssignRolesComponent, dialogConfig);

    // return dialogRef.afterClosed().subscribe((result) => {
    //   if (result) {
    //     this.toastr.success(
    //       'Member role(s) updated successfully',
    //       'Update Success'
    //     );
    //   }
    //   return;
    // });
  }

  onSearchTermChange(value: string) {
    this.store
      .select(
        memberSelector.getGroupMembersByType({
          memberType: [
            SchoolMemberType.teacher,
            SchoolMemberType.president,
            SchoolMemberType.principal,
          ],
          groupId: this.groupId,
          status: [MembershipStatus.active],
          filter: value,
        }),
      )
      .pipe(take(1))
      .subscribe((items) => {
        this.members$ = of(items);
      });
  }

  async onSearch(term: string) {
    this.members$ = of([]);
    this.isSearching$ = of(true);
    const result = await this.memberService.searchMembers(term, this.groupId!, {
      memberType: [
        SchoolMemberType.teacher,
        SchoolMemberType.president,
        SchoolMemberType.principal,
      ],
    });
    this.members$ = of(result);
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(true);
  }

  onCancelSearch() {
    this.loadMembers();
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(false);
  }

  onRefresh() {
    this.store.dispatch(
      memberActions.getMembers({
        source: 'ListFacultyComponent :: onRefresh',
        groupId: this.groupId!,
        status: [MembershipStatus.active],
        getMemberType: [
          SchoolMemberType.president,
          SchoolMemberType.principal,
          SchoolMemberType.teacher,
        ],
      }),
    );
  }

  onViewProfile(item: SchoolMember) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      user: item,
      title: `${item.name}'s Details`,
      description: `Profile details available in group`,
    };
    this.dialog.open(DlgViewProfileComponent, dialogConfig);
  }
}
